import { Plus, Trash2 } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { FieldInputProps } from './fieldInputProps'
import { emptyRow, MAX_ROWS, type RowValue } from './screenForm'

interface RowsFieldProps extends FieldInputProps<RowValue[]> {
  /** Renders one row's fields; the row's answers change through the setter it's given. */
  renderRow: (row: RowValue, setRow: (row: RowValue) => void) => ReactNode
}

/** Fixed rows the server names, or rows the customer adds and removes. */
export function RowsField({ field, value, onChange, id, disabled, renderRow }: RowsFieldProps) {
  const fixed = field.rows.length > 0
  const setRow = (next: RowValue) => onChange(value.map((row) => (row.id === next.id ? next : row)))
  // A list that needs a row opens with one, so its questions show as soon as it does.
  const needsFirstRow = !fixed && field.required && value.length === 0 && !disabled
  useEffect(() => {
    if (needsFirstRow) onChange([emptyRow(field, uuidv4())])
  }, [needsFirstRow]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div id={id} className="flex flex-col gap-3">
      {value.map((row, index) => (
        <Card key={row.id} className="gap-4 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold">
              {fixed
                ? (field.rows.find((r) => r.id === row.id)?.label ?? row.id)
                : `${field.itemLabel} ${index + 1}`}
            </span>
            {!fixed && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove ${field.itemLabel.toLowerCase()} ${index + 1}`}
                onClick={() => onChange(value.filter((r) => r.id !== row.id))}
              >
                <Trash2 />
              </Button>
            )}
          </div>
          {renderRow(row, setRow)}
        </Card>
      ))}
      {!fixed && !disabled && value.length < MAX_ROWS && (
        <Button
          type="button"
          variant="outline"
          className="h-auto min-h-10 self-start py-2 text-left whitespace-normal"
          onClick={() => onChange([...value, emptyRow(field, uuidv4())])}
        >
          <Plus />
          {field.addLabel || `Add ${field.itemLabel.toLowerCase()}`}
        </Button>
      )}
    </div>
  )
}
